import { useNavigate } from "react-router-dom";
import { useApp, formatCurrency } from "@/App";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, TrendingUp, TrendingDown, Wallet, Clock, PiggyBank } from "lucide-react";

export default function SummaryAnalytics() {
  const navigate = useNavigate();
  const { currentSummary, selectedCycle, getCycleDateRange } = useApp();

  const currentCycleRange = selectedCycle ? getCycleDateRange(selectedCycle) : null;

  const metrics = [
    { 
      title: "Total Income", 
      value: currentSummary?.totalIncome || 0, 
      icon: TrendingUp, 
      colorClass: "text-emerald-700", 
      bgClass: "bg-emerald-50", 
      borderClass: "border-emerald-200" 
    },
    { 
      title: "Total Expenses", 
      value: currentSummary?.totalExpenses || 0, 
      icon: TrendingDown, 
      colorClass: "text-rose-700", 
      bgClass: "bg-rose-50", 
      borderClass: "border-rose-200" 
    },
    { 
      title: "Net Balance", 
      value: currentSummary?.netBalance || 0, 
      icon: Wallet, 
      colorClass: (currentSummary?.netBalance || 0) >= 0 ? "text-indigo-700" : "text-rose-700", 
      bgClass: (currentSummary?.netBalance || 0) >= 0 ? "bg-indigo-50" : "bg-rose-50", 
      borderClass: (currentSummary?.netBalance || 0) >= 0 ? "border-indigo-200" : "border-rose-200" 
    },
    { 
      title: "Planned Bills", 
      value: currentSummary?.plannedBillsTotal || 0, 
      icon: Clock, 
      colorClass: "text-stone-700", 
      bgClass: "bg-stone-50", 
      borderClass: "border-stone-200" 
    },
    { 
      title: "Left to Pay", 
      value: currentSummary?.leftToPay || 0, 
      icon: Clock, 
      colorClass: "text-amber-700", 
      bgClass: "bg-amber-50", 
      borderClass: "border-amber-200" 
    },
    { 
      title: "Balance After Bills", 
      value: currentSummary?.balanceAfterBills || 0, 
      icon: PiggyBank, 
      colorClass: (currentSummary?.balanceAfterBills || 0) >= 0 ? "text-indigo-700" : "text-rose-700", 
      bgClass: (currentSummary?.balanceAfterBills || 0) >= 0 ? "bg-indigo-50" : "bg-rose-50", 
      borderClass: (currentSummary?.balanceAfterBills || 0) >= 0 ? "border-indigo-200" : "border-rose-200" 
    },
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="summary-analytics-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/')} className="rounded-full">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-indigo-100">
            <Wallet className="w-6 h-6 text-indigo-600" />
          </div>
          <div>
            <h2 className="font-heading text-3xl text-stone-900">Summary Analytics</h2>
            <p className="text-stone-500">{currentCycleRange?.label}</p>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {metrics.map((metric) => (
          <Card 
            key={metric.title}
            className={`p-6 rounded-2xl ${metric.bgClass} border ${metric.borderClass}`}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wider text-stone-500 mb-2">
                  {metric.title}
                </p>
                <p className={`font-body font-bold text-2xl md:text-3xl tracking-tighter ${metric.colorClass}`}>
                  {formatCurrency(metric.value)}
                </p>
              </div>
              <div className={`p-3 rounded-xl ${metric.bgClass}`}>
                <metric.icon className={`w-5 h-5 ${metric.colorClass}`} />
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Explanation Card */}
      <Card className="p-6 bg-white rounded-2xl border border-stone-200/60">
        <h3 className="font-semibold text-stone-900 mb-4">Understanding Your Numbers</h3>
        <div className="space-y-3 text-sm text-stone-600">
          <p><strong>Net Balance</strong> = Total Income - Total Expenses (what you actually have)</p>
          <p><strong>Balance After Bills</strong> = Total Income - Planned Bills (what you'll have after paying all planned bills)</p>
          <p><strong>Left to Pay</strong> = Unpaid Bills (what you still need to pay this cycle)</p>
        </div>
      </Card>
    </div>
  );
}
